import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  TextField,
} from "@fluentui/react";

const DeleteConfirmationDialog = ({
  isVisible,
  onDismiss,
  onConfirm,
  message,
  resourceName,
  resourceType,
  confirmationValue,
  confirmLabel,
  deleteButtonText = "Delete",
  cancelButtonText = "Cancel",
  isDeleting = false,
  errorMessage,
}) => {
  const [inputValue, setInputValue] = useState("");
  const cancelButtonRef = useRef(null);

  const normalizeLabel = (value, fallback = undefined) => {
    const trimmed = value?.trim();
    if (!trimmed) {
      return fallback;
    }
    return trimmed;
  };

  const formatTitleCase = (value) => {
    if (!value) {
      return "";
    }
    return value
      .split(" ")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const normalizedResourceType = useMemo(
    () => normalizeLabel(resourceType, ""),
    [resourceType]
  );

  const fallbackResourceType = useMemo(() => {
    if (!normalizedResourceType) {
      console.warn(
        "DeleteConfirmationDialog: `resourceType` is required. Defaulting to 'item'."
      );
      return "item";
    }

    return normalizedResourceType;
  }, [normalizedResourceType]);

  const formattedResourceType = useMemo(
    () => formatTitleCase(fallbackResourceType),
    [fallbackResourceType]
  );

  const expectedValue = useMemo(() => {
    const baseTarget = normalizeLabel(
      confirmationValue,
      normalizeLabel(resourceName, "delete")
    );

    if (baseTarget.length < 6) {
      return "delete";
    }

    if (baseTarget.length > 24) {
      return `delete ${fallbackResourceType}`;
    }

    return baseTarget;
  }, [confirmationValue, resourceName, fallbackResourceType]);

  const effectiveTitle = useMemo(
    () => `Delete ${formattedResourceType}`,
    [formattedResourceType]
  );

  const effectiveMessage = useMemo(() => {
    if (message) {
      return message;
    }

    const warningType = normalizedResourceType || (resourceName ? "resource" : "item");

    if (resourceName) {
      return `⚠️ Warning! Deleting the ${warningType} "${resourceName}" is irreversible. This action cannot be undone. Do you want to continue?`;
    }

    return `⚠️ Warning! Deleting this ${warningType} is irreversible. This action cannot be undone. Do you want to continue?`;
  }, [message, normalizedResourceType, resourceName]);

  const effectiveConfirmLabel = useMemo(() => {
    if (confirmLabel) {
      return confirmLabel;
    }

    return `Type "${expectedValue}" to confirm`;
  }, [confirmLabel, expectedValue]);

  useEffect(() => {
    if (isVisible) {
      setInputValue("");
      cancelButtonRef.current?.focus();
    }
  }, [isVisible, expectedValue]);

  const isDeleteDisabled =
    isDeleting || inputValue.trim() !== expectedValue.trim();

  return (
    <Dialog
      hidden={!isVisible}
      onDismiss={onDismiss}
      modalProps={{
        isBlocking: true,
      }}
      dialogContentProps={{
        type: DialogType.normal,
        title: effectiveTitle,
        subText: effectiveMessage,
      }}
    >
      {errorMessage ? (
        <MessageBar messageBarType={MessageBarType.error} isMultiline>
          {errorMessage}
        </MessageBar>
      ) : null}
      <TextField
        label={effectiveConfirmLabel}
        value={inputValue}
        onChange={(_, newValue) => setInputValue(newValue || "")}
      />
      <DialogFooter>
        <PrimaryButton
          text={deleteButtonText}
          onClick={onConfirm}
          disabled={isDeleteDisabled}
          styles={{ root: { backgroundColor: "#d13438", borderColor: "#d13438" } }}
        />
        <DefaultButton
          text={cancelButtonText}
          onClick={onDismiss}
          componentRef={cancelButtonRef}
        />
      </DialogFooter>
    </Dialog>
  );
};

export default DeleteConfirmationDialog;

DeleteConfirmationDialog.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onDismiss: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  message: PropTypes.string,
  resourceName: PropTypes.string,
  resourceType: PropTypes.string,
  confirmationValue: PropTypes.string,
  confirmLabel: PropTypes.string,
  deleteButtonText: PropTypes.string,
  cancelButtonText: PropTypes.string,
  isDeleting: PropTypes.bool,
  errorMessage: PropTypes.string,
};